import {
  createInertiaApp,
  http,
  router,
  type ResolvedComponent,
} from "@inertiajs/react"
import { putConfig, renderApp } from "@inertiaui/modal-react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

import type { Page } from "@inertiajs/core"
import type { Flash } from "@/types"
import { StrictMode, createElement, type ReactNode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"
import { startErrorReporting } from "@/lib/error-reporting"
import { pwaServiceWorkerPath } from "@/routes"

startErrorReporting()

type ModalAppArg = Parameters<typeof renderApp>[0]
type ModalPropsArg = Parameters<typeof renderApp>[1]

const appName = (import.meta.env.VITE_APP_NAME ?? "SERVOS") as string

const queryClient = new QueryClient()

// Lets a page load async content (an MDX module) before its first render, so SSR includes it.
const pages = import.meta.glob<{
  default: ResolvedComponent
  preload?: (page: Page) => Promise<void>
}>("../pages/**/*.tsx")

const hasCompleteServerTitle = (head: unknown) =>
  Array.isArray(head) && head.some((tag: string) => tag.startsWith("<title"))

void createInertiaApp({
  serverHead: true,

  title: (title, page) => {
    if (!title) return appName
    return hasCompleteServerTitle(page.props.head)
      ? title
      : `${title} - ${appName}`
  },

  resolve: async (name, page) => {
    const loader = pages[`../pages/${name}.tsx`]
    if (!loader) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }
    const { default: component, preload } = await loader()
    if (preload && page) await preload(page)
    component.layout ??= (page: ReactNode) =>
      createElement(PersistentLayout, null, page)
    return component
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
  },

  setup({ el, App, props }) {
    // renderApp wraps App with ModalStackProvider and a render-prop child.
    // Inertia's App type doesn't structurally match renderApp's expected
    // component shape, but the runtime is compatible — modal-react upstream
    // type is too narrow.
    const tree = createElement(
      StrictMode,
      null,
      createElement(
        QueryClientProvider,
        { client: queryClient },
        renderApp(
          App as unknown as ModalAppArg,
          props as unknown as ModalPropsArg,
        ),
      ),
    )
    if (el === null) {
      return tree
    }
    if (el.hasChildNodes()) {
      hydrateRoot(el, tree)
      return
    }
    createRoot(el).render(tree)
  },

  progress: {
    color: "var(--color-progress)",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  if (typeof document !== "undefined" && document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})

if (typeof window !== "undefined") {
  initializeTheme()
}

putConfig({
  type: "modal",
  navigate: true,
})

// inertiaui/modal fetches ModalLink pages itself and drops the response's
// shared props when the server redirected (e.g. an authorization redirect
// with a flash alert) — the flash never reaches the page store, so no toast
// appears. See https://github.com/inertiaui/modal/issues/263. Patch flash
// from every Inertia JSON response into the page store; normal router visits
// process the same flash twice, which useFlash dedupes via toast ids.
function flashFrom(data: unknown): Flash | null {
  try {
    const page: unknown = typeof data === "string" ? JSON.parse(data) : data
    if (typeof page !== "object" || page === null) return null
    const flash = (page as { props?: { flash?: Flash } }).props?.flash
    return flash && (flash.alert || flash.notice) ? flash : null
  } catch {
    return null
  }
}

http.onResponse((response) => {
  const flash = flashFrom(response.data)
  if (flash) router.replaceProp("flash", flash)
  return response
})

// Register a service worker on pages that opt in via `<link rel="manifest">`
// (see Staff::PwaMetaTags). The SW lives at the origin root. Skip in development:
// a caching SW fights Vite HMR and serves stale assets.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    if (!document.querySelector('link[rel="manifest"]')) return
    navigator.serviceWorker
      .register(pwaServiceWorkerPath(), { scope: "/" })
      .catch((error) => {
        console.error("Service worker registration failed:", error)
      })
  })
}
